import React, { useCallback, useMemo } from 'react';
import { Box, IconButton, Select, MenuItem, FormControl } from '@mui/material';
import {
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPreviousIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';

/**
 * Simplified navigation controls for comparison video session
 * Single row layout with skip controls, play button, and lap navigation
 */
function NavigationControlsNew({
  lapData = [],
  currentLap = 1,
  skipSeconds = 1,
  isPlaying = false,
  onNavigateToLap,
  onSkipSeconds,
  onSkipLap,
  onSetSkipSeconds,
  onPlay,
  onPause,
  className = '',
}) {
  // Find the fastest lap
  const fastestLap = useMemo(() => {
    if (!lapData || lapData.length === 0) return null;

    // Filter out synthetic laps (like out laps) and laps with invalid duration
    const validLaps = lapData.filter(
      (lap) =>
        !lap.synthetic &&
        lap.duration &&
        !isNaN(lap.duration) &&
        lap.duration > 0
    );

    if (validLaps.length === 0) return null;

    return validLaps.reduce((fastest, current) =>
      current.duration < fastest.duration ? current : fastest
    );
  }, [lapData]);

  // Format lap time for display
  const formatLapTime = useCallback((duration) => {
    if (!duration || isNaN(duration)) return '--:--';

    const minutes = Math.floor(duration / 60);
    const seconds = (duration % 60).toFixed(3);
    return `${minutes}:${seconds.padStart(6, '0')}`;
  }, []);

  // Handle lap navigation
  const handleLapChange = useCallback(
    (event) => {
      const lapNumber = parseInt(event.target.value);
      // const lapNumber = parseFloat(event.target.value);
      onNavigateToLap?.(lapNumber);
    },
    [onNavigateToLap]
  );

  const handlePreviousLap = useCallback(() => {
    const currentIndex = lapData.findIndex((lap) => lap.number === currentLap);
    if (currentIndex > 0) {
      onSkipLap?.(-1);
    }
  }, [lapData, currentLap, onSkipLap]);

  const handleNextLap = useCallback(() => {
    const currentIndex = lapData.findIndex((lap) => lap.number === currentLap);
    if (currentIndex < lapData.length - 1) {
      onSkipLap?.(1);
    }
  }, [lapData, currentLap, onSkipLap]);

  // Handle skip controls
  const handleSkipBackward = useCallback(() => {
    onSkipSeconds?.(-skipSeconds);
  }, [skipSeconds, onSkipSeconds]);

  const handleSkipForward = useCallback(() => {
    onSkipSeconds?.(skipSeconds);
  }, [skipSeconds, onSkipSeconds]);

  const handleSkipSecondsChange = useCallback(
    (event) => {
      onSetSkipSeconds?.(parseInt(event.target.value));
    },
    [onSetSkipSeconds]
  );

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      onPause?.();
    } else {
      onPlay?.();
    }
  }, [isPlaying, onPlay, onPause]);

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        p: 1,
      }}
    >
      {/* Playback Controls Group */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          // Adjust width as needed - can be controlled from parent or set here
          minWidth: 200, // You can adjust this value or remove it
          justifyContent: 'center',
        }}
      >
        {/* Skip backward */}
        <IconButton
          onClick={handleSkipBackward}
          size="small"
          sx={{ color: 'primary.main' }}
        >
          <NavigateBeforeIcon />
        </IconButton>

        {/* Skip seconds dropdown */}
        <FormControl size="small" sx={{ minWidth: 50 }}>
          <Select
            value={skipSeconds}
            onChange={handleSkipSecondsChange}
            displayEmpty
          >
            {/* <MenuItem value={0.1}>0.1s</MenuItem>
            <MenuItem value={0.5}>0.5s</MenuItem> */}
            <MenuItem value={1}>1s</MenuItem>
            <MenuItem value={5}>5s</MenuItem>
            <MenuItem value={10}>10s</MenuItem>
            <MenuItem value={15}>15s</MenuItem>
            <MenuItem value={30}>30s</MenuItem>
          </Select>
        </FormControl>

        {/* Play/Pause button */}
        <IconButton
          onClick={handlePlayPause}
          size="small"
          sx={{
            color: 'primary.main',
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        {/* Skip forward */}
        <IconButton
          onClick={handleSkipForward}
          size="small"
          sx={{ color: 'primary.main' }}
        >
          <NavigateNextIcon />
        </IconButton>
      </Box>

      {/* Lap Navigation Group */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Previous lap */}
        <IconButton
          onClick={handlePreviousLap}
          size="small"
          disabled={!lapData.length || currentLap === lapData[0]?.number}
          sx={{ color: 'primary.main' }}
        >
          <SkipPreviousIcon />
        </IconButton>

        {/* Lap selector */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select value={currentLap} onChange={handleLapChange} displayEmpty>
            {lapData.map((lap) => {
              const isFastest = fastestLap && lap.number === fastestLap.number;
              return (
                <MenuItem
                  key={lap.number}
                  value={lap.number}
                  sx={{
                    ...(isFastest && {
                      backgroundColor: 'success.light',
                      color: 'success.contrastText',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: 'success.main',
                      },
                    }),
                  }}
                >
                  Lap {lap.number} - {formatLapTime(lap.duration)}
                  {isFastest && ' 🏆'}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Next lap */}
        <IconButton
          onClick={handleNextLap}
          size="small"
          disabled={
            !lapData.length ||
            currentLap === lapData[lapData.length - 1]?.number
          }
          sx={{ color: 'primary.main' }}
        >
          <SkipNextIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

export default NavigationControlsNew;
